import { useState, useEffect } from "react";
import { Mail, ChevronRight, ArrowLeft, Shield, CheckCircle, Lock } from "lucide-react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { authService } from "../../services/authService";
import { alertError, alertSuccess } from "../../helpers/alertError";

const VerifyOtpPage = () => {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [emailInput, setEmailInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [timer, setTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    // Récupérer l'email depuis location.state
    const emailFromState = location.state?.email || "";

    // Si email est undefined ou null, on affiche le champ email
    const showEmailField = !emailFromState;

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };
        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    // Timer pour le renvoi du code
    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            setCanResend(true);
        }
    }, [timer]);

    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(0, 1);
        setOtp(newOtp);

        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            if (prevInput) prevInput.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").slice(0, 6);
        if (/^\d*$/.test(pastedData)) {
            const newOtp = [...otp];
            for (let i = 0; i < pastedData.length; i++) {
                newOtp[i] = pastedData[i];
            }
            setOtp(newOtp);
            const lastIndex = Math.min(pastedData.length - 1, 5);
            const lastInput = document.getElementById(`otp-${lastIndex}`);
            if (lastInput) lastInput.focus();
        }
    };

    const handleSubmit = async () => {
        const otpCode = otp.join("");

        if (otpCode.length !== 6) {
            alertError("Veuillez saisir le code complet à 6 chiffres.");
            return;
        }

        // Si on a un champ email, vérifier qu'il est rempli
        if (showEmailField && !emailInput) {
            alertError("Veuillez saisir votre adresse email.");
            return;
        }

        if (showEmailField && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput)) {
            alertError("Veuillez saisir une adresse email valide.");
            return;
        }

        setIsLoading(true);

        const emailToUse = showEmailField ? emailInput : emailFromState;

        try {
            const response = await authService.verifyOtp({
                email: emailToUse,
                otp: otpCode
            });

            if (response?.statusCode === 200) {
                navigate("/company-infos");
            } else {
                alertError(response?.message || "Code invalide. Veuillez réessayer.");
                setOtp(["", "", "", "", "", ""]);
                document.getElementById("otp-0")?.focus();
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || "Erreur de vérification. Veuillez réessayer.";
            alertError(errorMessage);
            setOtp(["", "", "", "", "", ""]);
            document.getElementById("otp-0")?.focus();
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (!canResend) return;

        const emailToUse = showEmailField ? emailInput : emailFromState;

        if (!emailToUse) {
            alertError("Veuillez saisir votre adresse email.");
            return;
        }

        try {
            const response = await authService.resendOtp({ email: emailToUse });

            if (response?.statusCode === 200) {
                alertSuccess("Un nouveau code a été envoyé à votre adresse email.");
                setTimer(60);
                setCanResend(false);
                setOtp(["", "", "", "", "", ""]);
                document.getElementById("otp-0")?.focus();
            } else {
                alertError(response?.message || "Erreur lors du renvoi du code.");
            }
        } catch (error: any) {
            alertError("Erreur lors du renvoi du code. Veuillez réessayer.");
        }
    };

    return (
        <div className="flex min-h-screen bg-white font-sans antialiased select-none">
            {/* PANEL GAUCHE */}
            <div className="relative hidden w-1/2 bg-[#031B4E] lg:block overflow-hidden">
                <div
                    className="absolute inset-0 opacity-5"
                    style={{
                        backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
                        backgroundSize: "32px 32px"
                    }}
                />

                <div
                    className="absolute w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none transition-all duration-500 ease-out"
                    style={{
                        top: `${mousePosition.y - 300}px`,
                        left: `${mousePosition.x - 300}px`,
                    }}
                />

                <div className="relative flex h-full flex-col justify-between p-16 text-white">
                    <Link to={"/"} className="flex items-center gap-2">
                        <img src="/logo.png" alt="IZI Call Center" className="h-12 w-auto" />
                        <div>
                            <div className="text-xl font-black tracking-wider leading-none">
                                <span className="text-white">Izi</span> <span className="text-[#FFB800]">CALL CENTER</span>
                            </div>
                            <div className="text-[9px] tracking-[0.25em] text-gray-300 font-medium uppercase mt-0.5">
                                Connecter • Assister • Satisfaire
                            </div>
                        </div>
                    </Link>

                    <div className="max-w-xl space-y-8">
                        <div className="space-y-4">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-3 py-1 text-xs font-medium text-blue-300 border border-blue-500/20">
                                Étape 2 sur 2
                            </span>
                            <h1 className="text-5xl font-extrabold tracking-tight leading-[1.15] text-white">
                                Sécurisez votre <br />
                                <span className="text-blue-400">inscription</span>
                            </h1>
                            <p className="text-lg text-slate-300/90 leading-relaxed font-light">
                                Saisissez le code de vérification reçu par email pour finaliser votre inscription.
                            </p>
                        </div>

                        <div className="space-y-4 pt-4 border-t border-white/5">
                            <div className="flex items-start gap-4">
                                <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-md bg-white/5 border border-white/10 text-blue-400">
                                    <Shield className="h-3.5 w-3.5" />
                                </div>
                                <p className="text-sm text-slate-300 font-medium">Vérification en deux étapes pour plus de sécurité</p>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-md bg-white/5 border border-white/10 text-blue-400">
                                    <Mail className="h-3.5 w-3.5" />
                                </div>
                                <p className="text-sm text-slate-300 font-medium">
                                    {showEmailField 
                                        ? "Saisissez votre email pour vérifier votre compte" 
                                        : `Code envoyé à ${emailFromState}`
                                    }
                                </p>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-md bg-white/5 border border-white/10 text-blue-400">
                                    <CheckCircle className="h-3.5 w-3.5" />
                                </div>
                                <p className="text-sm text-slate-300 font-medium">Validez et accédez à la configuration de votre compte</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between text-xs border-t border-white/5 pt-6">
                        <div className="flex items-center gap-4">
                            <Link to="/mentions-legales" className="text-slate-400/70 hover:text-white transition-colors">
                                Mentions légales
                            </Link>
                            <span className="text-slate-500/30">•</span>
                            <Link to="/confidentialite" className="text-slate-400/70 hover:text-white transition-colors">
                                Confidentialité
                            </Link>
                            <span className="text-slate-500/30">•</span>
                            <Link to="/cgu" className="text-slate-400/70 hover:text-white transition-colors">
                                CGU
                            </Link>
                        </div>
                        <span className="text-slate-500/50">© 2026</span>
                    </div>
                </div>
            </div>

            {/* PANEL DROIT */}
            <div className="flex w-full flex-col justify-between px-6 py-10 lg:w-1/2 sm:px-16 lg:px-24 bg-white">
                <div className="flex items-center justify-between lg:hidden mb-8">
                    <div className="flex items-center gap-2.5">
                        <img src="/logo.png" alt="Izi CallCenter" className="h-8 w-auto" />
                        <span className="text-lg font-bold text-[#031B4E]">Izi CallCenter</span>
                    </div>
                </div>

                <div className="hidden lg:block h-8" />

                <div className="mx-auto w-full max-w-md space-y-8 my-auto">
                    <div className="space-y-2 text-center lg:text-left">
                        <div className="flex items-center gap-2 justify-center lg:justify-start">
                            <Shield className="h-8 w-8 text-blue-600" />
                            <h2 className="text-3xl font-bold tracking-tight text-[#031B4E]">
                                Vérification
                            </h2>
                        </div>
                        <p className="text-sm text-slate-500 font-light">
                            Saisissez le code à 6 chiffres reçu par email
                        </p>
                    </div>

                    <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                        {/* Email - visible uniquement si email est undefined ou null */}
                        {showEmailField && (
                            <div className="space-y-1.5">
                                <label htmlFor="email" className="text-xs font-semibold text-slate-700 tracking-wide uppercase">
                                    Adresse email
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <input
                                        id="email"
                                        type="email"
                                        value={emailInput}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                                        placeholder="contact@entreprise.com"
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {/* Code OTP */}
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-700 tracking-wide uppercase block text-center lg:text-left">
                                Code de vérification
                            </label>
                            <div className="flex justify-center gap-2 lg:gap-3">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        id={`otp-${index}`}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        onPaste={index === 0 ? handlePaste : undefined}
                                        className="w-12 h-14 sm:w-14 sm:h-16 text-center text-xl font-bold rounded-xl border-2 border-slate-200 bg-slate-50/50 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Timer et Renvoi */}
                        <div className="flex flex-col items-center gap-3">
                            <button
                                type="button"
                                onClick={handleResendCode}
                                disabled={!canResend}
                                className={`text-sm font-medium transition-colors ${canResend
                                    ? "text-blue-600 hover:text-blue-800 cursor-pointer"
                                    : "text-slate-400 cursor-not-allowed"
                                }`}
                            >
                                {canResend ? "Renvoyer le code" : `Renvoyer dans ${timer}s`}
                            </button>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#031B4E] px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/10 hover:bg-[#052975] focus:outline-none focus:ring-4 focus:ring-blue-600/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center">
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span className="ml-2 font-medium">Vérification en cours...</span>
                                </div>
                            ) : (
                                <>
                                    <span>Vérifier et continuer</span>
                                    <ChevronRight className="h-4 w-4" />
                                </>
                            )}
                        </button>

                        {/* Retour à l'inscription */}
                        <div className="text-center pt-2">
                            <Link
                                to="/register"
                                className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-[#031B4E] transition-colors gap-1"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" />
                                Modifier mes informations
                            </Link>
                        </div>
                    </form>
                </div>

                <div className="text-center text-xs text-slate-400 mt-8">
                    © 2026 Izi CallCenter. Tous droits réservés.
                </div>
            </div>
        </div>
    );
};

export default VerifyOtpPage;